import React, { useState } from 'react';
import { Button } from 'components/Button';
import { Select } from 'components/Select/Select';
import { IOptions } from 'components/Select/Select.types';
import AnalystService from 'services/ApiService/AnalystService/analystService';
import { IInspectionsSelected } from 'pages/ManagementDashboard/components/InspectionsTable/InspectionsTable.types';
import { toast } from 'react-toastify';
import { mutateMany } from 'hooks/SWRUtils/SWRUtils';
import { IAnalyst as IAnalystService } from 'services/ApiService/AnalystService/analystService.types';
import { IAssociateAnalystDialogProps } from './AssociateAnalystDialog.types';
import { DialogBase, Title, Description, ContainerButton, MessageError } from './AssociateAnalystDialog.styles';

const AssociateAnalystDialog: React.FC<IAssociateAnalystDialogProps> = ({
  inspectionsSelected,
  dialogVisibility,
  handleClose,
  setInspectionsSelected,
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [analystsList, setAnalystsList] = useState<IOptions[]>([]);
  const [analystSelected, setAnalystSelected] = useState<string>('');

  const resetSelect = (): void => {
    setAnalystsList([]);
    setAnalystSelected('');
  };

  const closeDialog = (): void => {
    resetSelect();
    handleClose();
  };

  const totalInspections = (): string => {
    return inspectionsSelected.length > 1 ? `${inspectionsSelected.length} atendimentos` : '1 atendimento';
  };

  const isAnalystNotSelected = analystSelected === '';
  const isDisabled = isAnalystNotSelected || loading;

  const getComapaniesIdsOfInspectionsSelected = (): string[] => {
    const companiesId = inspectionsSelected.map((inspection) => inspection.companyId);
    return companiesId.filter((company, index) => companiesId.indexOf(company) === index);
  };

  const isAnalystsAvailable = (): boolean => analystsList.length > 0;

  const analystsToOptions = (analysts: IAnalystService[]): IOptions[] => {
    return analysts.map(({ id: value, username: name }) => ({ value, name }));
  };

  const getAnalysts = async (companiesIds: string[]): Promise<IOptions[]> => {
    try {
      const analysts = await AnalystService.getAnalysts(companiesIds);

      return analystsToOptions(analysts);
    } catch (error) {
      toast.error('Problema ao carregar os analistas, tente novamente.');

      return [];
    }
  };

  const instantiateAnalystsInModal = async (): Promise<void> => {
    const analysts = await getAnalysts(getComapaniesIdsOfInspectionsSelected());
    setAnalystsList(analysts);
    setAnalystSelected(analysts[0]?.value);
  };

  const getInspectionsIds = (inspections: IInspectionsSelected[]): string[] => {
    return inspections.map((inspection) => inspection.id);
  };

  const setAnalystToInspection = async (): Promise<void> => {
    try {
      setLoading(true);
      await AnalystService.putAnalystInInspections(analystSelected, getInspectionsIds(inspectionsSelected));

      mutateMany('^inspections\\/admin\\?companiesId=.*');
      toast.success('Atendimentos associados com sucesso.');
      setInspectionsSelected([]);
      setLoading(false);
      closeDialog();
    } catch (error) {
      toast.error('Atendimentos não associados, tente novamente.');
      setInspectionsSelected([]);
      setLoading(false);
    }
  };

  return (
    <DialogBase
      data-testid="associate-analyst-dialog"
      open={dialogVisibility}
      onClose={closeDialog}
      TransitionProps={{ onEnter: instantiateAnalystsInModal }}
    >
      <Title>Associar ao analista</Title>
      <Description>Você está associando {totalInspections()}</Description>

      {isAnalystsAvailable() ? (
        <Select
          testID="analyst-select"
          options={analystsList}
          selectedOptions={analystSelected}
          label="Analista"
          labelId="analyst"
          onChange={({ target: { value } }): void => setAnalystSelected(value as string)}
        />
      ) : (
        <MessageError>Nenhum analista encontrado</MessageError>
      )}

      <ContainerButton>
        <Button
          testID="associate-analyst-cancel"
          type="button"
          variant="outlined"
          onClick={closeDialog}
          text="CANCELAR"
        />
        <Button
          testID="associate-analyst-submit"
          type="button"
          text="ASSOCIAR"
          loading={loading}
          loadingSize={25}
          loadingColor="#FFF"
          disabled={isDisabled}
          onClick={setAnalystToInspection}
        />
      </ContainerButton>
    </DialogBase>
  );
};

export default AssociateAnalystDialog;
